import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PageSEO from '@/components/seo/PageSEO';
import { ArrowRight, CheckCircle, Trophy, Rocket, Users } from 'lucide-react';
const structuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Hoe werkt leadgeneratie voor KMO's?",
  "description": "Leer hoe je als KMO nieuwe klanten aantrekt via slimme leadgeneratie. Ontdek onze aanpak in 3 praktische stappen.",
  "publisher": {
    "@type": "Organization",
    "name": "Afsprakenmaker.be",
    "logo": "https://afsprakenmaker.be/logo-2026.png"
  }
};
const LeadgeneratieKmo = () => {
  return <div className="min-h-screen flex flex-col">
      <PageSEO title="Hoe werkt leadgeneratie voor KMO's? – In 3 stappen naar meer klanten" description="Leer hoe je als KMO nieuwe klanten aantrekt via slimme leadgeneratie. Ontdek onze aanpak in 3 praktische stappen." keywords="leadgeneratie KMO, B2B leads, klanten aantrekken, leadgeneratie strategie, nieuwe klanten KMO" canonicalUrl="/leadgeneratie-kmo" structuredData={structuredData} />
      
      {/* Hero Section */}
      <section className="bg-brand-lightBlue pt-36 pb-16 md:pt-40 md:pb-24 relative">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" alt="Business growth strategy meeting" className="w-full h-full object-cover" />
        </div>
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Hoe werkt leadgeneratie voor KMO's?
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Leer hoe je als KMO structureel nieuwe klanten aantrekt via een slimme, meetbare aanpak.
            </p>
            <Link to="/contact" className="btn-primary">
              Vraag jouw gratis adviesgesprek aan
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="section">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Inleiding</h2>
              <p className="text-lg text-gray-700 mb-4">
                Veel bedrijven vertrouwen nog op aanbevelingen, netwerken of toevallig binnenkomende aanvragen. Maar als je echt wil groeien, heb je iets krachtigers nodig: een voorspelbare stroom aan gekwalificeerde leads.
              </p>
              <p className="text-lg text-gray-700">
                Leadgeneratie zorgt ervoor dat jouw bedrijf niet afhangt van geluk, maar van een slim systeem dat interesse opwekt, converteert en opvolgt.
              </p>
            </div>

            {/* What is lead generation */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Wat is leadgeneratie?</h2>
              <p className="text-lg text-gray-700 mb-6">
                Leadgeneratie is het proces waarbij je:
              </p>
              <div className="bg-brand-lightBlue rounded-lg p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="text-brand-blue mt-1">
                    <CheckCircle size={20} />
                  </div>
                  <p className="text-gray-700 text-lg">de juiste mensen aantrekt,</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-brand-blue mt-1">
                    <CheckCircle size={20} />
                  </div>
                  <p className="text-gray-700 text-lg">hun interesse wekt,</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-brand-blue mt-1">
                    <CheckCircle size={20} />
                  </div>
                  <p className="text-gray-700 text-lg">en hen omzet in concrete verkoopkansen.</p>
                </div>
              </div>
              <p className="text-lg text-gray-700 mt-6">
                Een lead is iemand die interesse toont in jouw aanbod — nog geen klant, maar iemand die openstaat voor contact of informatie.
              </p>
            </div>

            {/* Why is this important */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Waarom is dit belangrijk voor KMO's?</h2>
              <p className="text-lg text-gray-700 mb-6">
                Veel kleine bedrijven draaien op bestaande relaties. Maar:
              </p>
              <div className="space-y-3 mb-8">
                <p className="text-lg text-gray-700 italic">Wat als die opdrogen?</p>
                <p className="text-lg text-gray-700 italic">Wat als je wíl groeien?</p>
                <p className="text-lg text-gray-700 italic">Wat als je nieuwe markten wil aanboren?</p>
              </div>
              
              <p className="text-lg font-semibold mb-4">Leadgeneratie:</p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-brand-blue rounded-full p-1 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Zorgt voor een constante instroom aan opportuniteiten</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-brand-blue rounded-full p-1 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Maakt je verkoop voorspelbaarder</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-brand-blue rounded-full p-1 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Zorgt dat je team enkel met geïnteresseerden praat</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-brand-blue rounded-full p-1 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Bespaart tijd en energie in de prospectiefase</p>
                </div>
              </div>
            </div>

            {/* 3-step approach */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Onze aanpak in 3 stappen</h2>
              <p className="text-lg text-gray-700 mb-8">
                Wij combineren data, psychologie en slimme opvolging in één krachtig systeem:
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                  <div className="bg-brand-blue text-white rounded-full w-10 h-10 flex items-center justify-center mb-4 text-lg font-bold">1</div>
                  <h3 className="text-xl font-semibold mb-3">Doelgroepanalyse</h3>
                  <p className="text-gray-700">
                    We bepalen wie jouw ideale klant is en waar die zich bevindt — online én offline.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                  <div className="bg-brand-blue text-white rounded-full w-10 h-10 flex items-center justify-center mb-4 text-lg font-bold">2</div>
                  <h3 className="text-xl font-semibold mb-3">Digitale lead-campagnes</h3>
                  <p className="text-gray-700">We zetten strategische Tools in dat website bezoekers kunnen monitoren en bedrijven identificeren via BTW nr.</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                  <div className="bg-brand-blue text-white rounded-full w-10 h-10 flex items-center justify-center mb-4 text-lg font-bold">3</div>
                  <h3 className="text-xl font-semibold mb-3">Persoonlijke opvolging</h3>
                  <p className="text-gray-700">
                    Onze call agents nemen contact op met geïnteresseerden, filteren ze en maken een afspraak in jouw agenda.
                  </p>
                </div>
              </div>
              
              <div className="bg-brand-lightBlue p-6 rounded-lg mt-8">
                <h3 className="text-xl font-semibold mb-2">Resultaat?</h3>
                <p className="text-lg text-gray-700">
                  Geen koude prospectie meer. Enkel warme, geïnteresseerde leads.
                </p>
              </div>
            </div>

            {/* Case study */}
            <div className="mb-12 bg-white p-8 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="text-brand-blue">
                  <Trophy size={32} />
                </div>
                <h2 className="text-2xl font-bold">Klant in de kijker: Een concrete case</h2>
              </div>
              <p className="text-lg text-gray-700 mb-6">Voor een lokale verzekeringsmakelaar genereerden we 33 warme leads in 4 weken. Daarvan zaten er 12 binnen de 2 maanden in offertefase, met een potentiële omzet van €18.000 jaarlijkse commissie.</p>
            </div>

            {/* Final CTA */}
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-bold mb-4">Wil jij ook voorspelbaar groeien?</h2>
              <p className="text-lg text-gray-700 mb-8">
                Stop met hopen op toevallig succes. Bouw vandaag nog aan een stabiele verkoopmachine via slimme leadgeneratie.
              </p>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Ontdek jouw groeikansen
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default LeadgeneratieKmo;